// Rollable tables (issue 1158): the DM's Tables tool. The site's tables for the campaign's game system and the DM's own
// (kept with the account, at every table they run), each with Roll; the DM's own can be built here — a name, outcomes
// with text and a weight from 1 to 100, and optionally the creature an outcome names, chosen from the compendium. The
// roll is the server's: the result arrives in the dice bag's log, private or public by the dice bag's Private choice,
// and an outcome naming a creature offers "Place <creature>" on the DM's line there. Quick NPC (issue 1157) rolls a given
// name, a family name and a trait from the tables the DM picks, shows the NPC on a card and saves it to the campaign's NPCs.
import { useCallback, useEffect, useState } from 'react';
import { TABLE_LIMITS, parseUserTable, type TableOutcome, type TableSource, type UserTable } from '@dragons-table/vtt-shared';
import { useShell } from '../context';
import { useDicePrivate } from '../../dicePrivacy';
import { deleteTable, myTables, rollTable, saveTable, siteTables, type SiteTable } from '../../rolltables';
import { searchCompendium, type CompendiumHit } from '../../compendium';
import { placeableSegments } from '../../place';
import { SLOTS, npcName, rememberChoices, rememberedChoices, rollQuickNpc, starterFor, type Choices, type QuickNpc, type Slot } from '../../quickNpc';
import { SaveNpcDialog } from './SaveNpcDialog';
import { ConfirmDialog } from '../Dialogs';
import { BTN } from '../ui';

const btn = `${BTN} shrink-0`;
type Loaded<T> = { kind: 'loading' } | { kind: 'error'; reason: string } | { kind: 'ok'; value: T };
type Draft = { id: string | null; name: string; outcomes: Array<{ text: string; weight: string; ref?: TableOutcome['ref'] }> };
const blank = (): Draft => ({ id: null, name: '', outcomes: [{ text: '', weight: '1' }] });
const DOWN = "The tables can't be read just now. Try again in a moment.";

export function TablesPanel() {
  const { campaignId } = useShell();
  const [priv, setPriv] = useDicePrivate();
  const [site, setSite] = useState<Loaded<SiteTable[]>>({ kind: 'loading' });
  const [mine, setMine] = useState<Loaded<UserTable[]>>({ kind: 'loading' });
  const [draft, setDraft] = useState<Draft | null>(null);
  const [said, setSaid] = useState('');
  const [busy, setBusy] = useState(false);
  const [deleting, setDeleting] = useState<UserTable | null>(null);

  const load = useCallback(async () => {
    const [s, m] = await Promise.all([siteTables(campaignId), myTables()]);
    setSite(s.kind === 'ok' ? s : { kind: 'error', reason: s.kind === 'refused' ? s.reason : DOWN });
    setMine(m.kind === 'ok' ? m : { kind: 'error', reason: m.kind === 'refused' ? m.reason : DOWN });
  }, [campaignId]);
  useEffect(() => { void load(); }, [load]);

  const roll = async (table: TableSource, name: string) => {
    setBusy(true);
    const got = await rollTable(campaignId, table, priv);
    setBusy(false);
    setSaid(got.kind === 'ok' ? `Rolled ${name}${priv ? ', privately' : ''}: the result is in the log.` : got.kind === 'refused' ? got.reason : "That table couldn't be rolled just now.");
  };

  const remove = async (t: UserTable) => {
    const got = await deleteTable(t.id);
    if (got.kind === 'ok') { setSaid(`Deleted ${t.name}.`); void load(); } else setSaid(got.kind === 'refused' ? got.reason : DOWN);
  };

  return (
    <div className="flex h-full flex-col gap-2 overflow-y-auto p-2 text-sm" data-testid="tables">
      <label className={`flex items-center gap-1 self-start rounded px-2 py-0.5 text-xs font-semibold ${priv ? 'bg-rose-900 text-rose-100' : 'bg-emerald-900 text-emerald-100'}`} data-testid="tables-privacy"
        title="The dice bag's Private choice: a table rolled private posts to your log alone.">
        <input type="checkbox" aria-label="Private" checked={priv} onChange={(e) => setPriv(e.target.checked)} />
        {priv ? '🕶 PRIVATE — only you see the results' : '👁 PUBLIC — the table sees the results'}
      </label>
      {said && <p role="status" className="text-xs text-stone-300" data-testid="tables-said">{said}</p>}

      {site.kind === 'ok' && mine.kind === 'ok' && <QuickNpcSection site={site.value} mine={mine.value} priv={priv} />}

      <section aria-label="Your tables">
        <div className="flex items-center gap-2">
          <h3 className="vtt-section-head flex-1">Your tables</h3>
          <button type="button" className={btn} data-testid="tables-new" onClick={() => setDraft(blank())}
            disabled={mine.kind === 'ok' && mine.value.length >= TABLE_LIMITS.tables}>New table</button>
        </div>
        {mine.kind === 'loading' && <p className="text-stone-400" role="status">Reading your tables…</p>}
        {mine.kind === 'error' && <p className="text-amber-300" role="alert">{mine.reason}</p>}
        {mine.kind === 'ok' && mine.value.length === 0 && <p className="text-xs text-stone-500" data-testid="tables-mine-empty">None yet. A table you build here goes with you to every table you run.</p>}
        {mine.kind === 'ok' && mine.value.length > 0 && (
          <ul className="divide-y divide-stone-800" data-testid="tables-mine">
            {mine.value.map((t) => (
              <li key={t.id} className="flex items-center gap-1 py-1" data-testid={`tables-mine-${t.id}`}>
                {/* A long name wraps in a narrow bar rather than being cut, and the outcome count keeps its own place. */}
                <span className="min-w-0 flex-1 break-words" title={t.name}>{t.name}</span>
                <span className="shrink-0 text-xs text-stone-500" data-testid={`tables-mine-count-${t.id}`}
                  title={`${t.outcomes.length} ${t.outcomes.length === 1 ? 'outcome' : 'outcomes'}`}>· {t.outcomes.length}</span>
                <button type="button" className={btn} disabled={busy} aria-label={`Roll ${t.name}`} data-testid={`tables-roll-mine-${t.id}`} onClick={() => void roll({ source: 'mine', id: t.id }, t.name)}>Roll</button>
                <button type="button" className={btn} aria-label={`Edit ${t.name}`} data-testid={`tables-edit-${t.id}`}
                  onClick={() => setDraft({ id: t.id, name: t.name, outcomes: t.outcomes.map((o) => ({ text: o.text, weight: String(o.weight), ...(o.ref ? { ref: o.ref } : {}) })) })}>Edit</button>
                <button type="button" className={btn} aria-label={`Delete ${t.name}`} data-testid={`tables-delete-${t.id}`} onClick={() => setDeleting(t)}>Delete</button>
              </li>
            ))}
          </ul>
        )}
      </section>

      {deleting && (
        <ConfirmDialog title={`Delete ${deleting.name}?`} body="This can't be undone." confirmLabel="Delete" destructive
          onClose={() => setDeleting(null)} onConfirm={() => { const t = deleting; setDeleting(null); void remove(t); }} />
      )}

      {draft && <Editor draft={draft} onCancel={() => setDraft(null)} onSaved={(t) => { setDraft(null); setSaid(`Saved ${t.name}.`); void load(); }} />}

      <section aria-label="The site's tables">
        <h3 className="vtt-section-head">The site's tables</h3>
        {site.kind === 'loading' && <p className="text-stone-400" role="status">Reading the site's tables…</p>}
        {site.kind === 'error' && <p className="text-amber-300" role="alert">{site.reason}</p>}
        {site.kind === 'ok' && site.value.length === 0 && <p className="text-xs text-stone-500">This game system has no tables on the site.</p>}
        {site.kind === 'ok' && site.value.length > 0 && (
          <ul className="divide-y divide-stone-800" data-testid="tables-site">
            {site.value.map((t) => (
              <li key={t.slug} className="flex items-center gap-1 py-1" data-testid={`tables-site-${t.slug}`}>
                <span className="min-w-0 flex-1 break-words" title={t.name}>{t.name}</span>
                <button type="button" className={btn} disabled={busy} aria-label={`Roll ${t.name}`} data-testid={`tables-roll-site-${t.slug}`} onClick={() => void roll({ source: 'site', slug: t.slug }, t.name)}>Roll</button>
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}

const keyOf = (t: TableSource | null | undefined): string => (!t ? '' : t.source === 'site' ? `site:${t.slug}` : `mine:${t.id}`);
const sourceOf = (k: string): TableSource | null => (k.startsWith('site:') ? { source: 'site', slug: k.slice(5) } : k.startsWith('mine:') ? { source: 'mine', id: k.slice(5) } : null);

/** Quick NPC (issue 1157): three ordinary table rolls, one per slot, from the tables the DM picks; the card and its Save. */
function QuickNpcSection({ site, mine, priv }: { site: SiteTable[]; mine: UserTable[]; priv: boolean }) {
  const { campaignId } = useShell();
  const exists = (t: TableSource | undefined) => !!t && (t.source === 'site' ? site.some((x) => x.slug === t.slug) : mine.some((x) => x.id === t.id));
  const [choices, setChoices] = useState<Choices>(() => {
    const kept = rememberedChoices();
    const out: Choices = {};
    for (const { key } of SLOTS) { const c = exists(kept[key]) ? kept[key] : starterFor(key, site); if (c) out[key] = c; }
    return out;
  });
  const [npc, setNpc] = useState<QuickNpc | null>(null);
  const [said, setSaid] = useState('');
  const [saved, setSaved] = useState('');
  const [busy, setBusy] = useState(false);
  const [saving, setSaving] = useState(false);
  const choose = (slot: Slot, k: string) => {
    const next = { ...choices }; const t = sourceOf(k);
    if (t) next[slot] = t; else delete next[slot];
    setChoices(next); rememberChoices(next);
  };
  const nameOf = (t: TableSource | undefined) => (!t ? '' : t.source === 'site' ? site.find((x) => x.slug === t.slug)?.name ?? '' : mine.find((x) => x.id === t.id)?.name ?? '');
  const ready = SLOTS.every(({ key }) => !!choices[key]);
  const roll = async () => {
    if (!ready) return;
    setBusy(true); setSaid(''); setSaved('');
    const got = await rollQuickNpc(campaignId, choices as Record<Slot, TableSource>, priv);
    setBusy(false);
    if (got.kind === 'ok') setNpc(got.value); else setSaid(got.kind === 'refused' ? got.reason : "That table couldn't be rolled just now.");
  };
  return (
    <section aria-label="Quick NPC" className="space-y-1 rounded border border-stone-800 p-2" data-testid="quick-npc">
      <h3 className="vtt-section-head">Quick NPC</h3>
      {SLOTS.map(({ key, label }) => (
        <label key={key} className="flex items-center gap-1 text-xs">
          <span className="w-20 shrink-0 text-stone-400">{label}</span>
          {/* Room on the right for the arrow, so a long name ("Family Names and Bynames") never runs under it; the whole name on hover. */}
          <select aria-label={`${label} table`} data-testid={`quick-npc-${key}`} className="min-w-0 flex-1 truncate rounded bg-stone-800 py-0.5 pl-1 pr-6" value={keyOf(choices[key])} onChange={(e) => choose(key, e.target.value)}
            title={nameOf(choices[key]) || undefined}>
            <option value="">Choose a table…</option>
            {site.length > 0 && <optgroup label="The site's">{site.map((t) => <option key={t.slug} value={`site:${t.slug}`}>{t.name}</option>)}</optgroup>}
            {mine.length > 0 && <optgroup label="Yours">{mine.map((t) => <option key={t.id} value={`mine:${t.id}`}>{t.name}</option>)}</optgroup>}
          </select>
        </label>
      ))}
      <button type="button" className={btn} data-testid="quick-npc-roll" disabled={busy || !ready} onClick={() => void roll()}
        title={priv ? 'Three private rolls: only you see them.' : 'Three public rolls: the table sees them.'}>Roll a quick NPC</button>
      {said && <p role="alert" className="text-xs text-amber-300" data-testid="quick-npc-said">{said}</p>}
      {saved && <p role="status" className="text-xs text-stone-300" data-testid="quick-npc-saved">{saved}</p>}
      {npc && (
        <div className="space-y-1 rounded bg-stone-800/60 p-2" data-testid="quick-npc-card">
          <p><span className="font-semibold">{npcName(npc)}</span> — {npc.trait}</p>
          <button type="button" className={btn} data-testid="quick-npc-save" aria-label={`Save ${npcName(npc)} to the campaign's NPCs`} onClick={() => setSaving(true)}>Save to the campaign…</button>
        </div>
      )}
      {saving && npc && <SaveNpcDialog name={npcName(npc)} note={npc.trait} onClose={() => setSaving(false)} onSaved={(n) => setSaved(`Saved ${n} to the campaign's NPCs.`)} />}
    </section>
  );
}

/** Building or changing one of the DM's own tables: checked by the shared parser before it is sent. */
function Editor({ draft: first, onCancel, onSaved }: { draft: Draft; onCancel: () => void; onSaved: (t: UserTable) => void }) {
  const [d, setD] = useState<Draft>(first);
  const [said, setSaid] = useState('');
  const [picking, setPicking] = useState<number | null>(null);
  const set = (i: number, patch: Partial<Draft['outcomes'][number]>) => setD({ ...d, outcomes: d.outcomes.map((o, k) => (k === i ? { ...o, ...patch } : o)) });
  const save = async () => {
    const table = parseUserTable({ name: d.name, outcomes: d.outcomes.map((o) => ({ text: o.text, weight: Number(o.weight), ...(o.ref ? { ref: o.ref } : {}) })) });
    if (!table) { setSaid('Every outcome needs some text and a weight from 1 to 100, and the table a name.'); return; }
    const got = await saveTable(table, d.id);
    if (got.kind === 'ok') onSaved(got.value); else setSaid(got.kind === 'refused' ? got.reason : DOWN);
  };
  return (
    <form noValidate className="space-y-2 rounded border border-stone-700 p-2" data-testid="tables-editor" onSubmit={(e) => { e.preventDefault(); void save(); }}>
      <input aria-label="Table name" data-testid="tables-edit-name" placeholder="Wandering monsters" maxLength={TABLE_LIMITS.name}
        className="w-full rounded bg-stone-800 px-2 py-1" value={d.name} onChange={(e) => setD({ ...d, name: e.target.value })} />
      <ol className="space-y-1">
        {d.outcomes.map((o, i) => (
          <li key={i} className="space-y-1" data-testid={`tables-outcome-${i}`}>
            <div className="flex items-center gap-1">
              <input aria-label={`Outcome ${i + 1}`} data-testid={`tables-outcome-text-${i}`} maxLength={TABLE_LIMITS.text}
                className="min-w-0 flex-1 rounded bg-stone-800 px-2 py-1" value={o.text} onChange={(e) => set(i, { text: e.target.value })} />
              <input aria-label={`Weight of outcome ${i + 1}`} data-testid={`tables-outcome-weight-${i}`} type="number" min={1} max={TABLE_LIMITS.weight}
                className="w-14 rounded bg-stone-800 px-1 py-1" value={o.weight} onChange={(e) => set(i, { weight: e.target.value })} />
              <button type="button" className={btn} aria-label={`Remove outcome ${i + 1}`} disabled={d.outcomes.length < 2}
                onClick={() => setD({ ...d, outcomes: d.outcomes.filter((_, k) => k !== i) })}>✕</button>
            </div>
            <div className="flex items-center gap-1 text-xs">
              {o.ref
                ? <span className="text-stone-300" data-testid={`tables-outcome-ref-${i}`}>Places {o.ref.name} <button type="button" className={btn} onClick={() => set(i, { ref: undefined })}>Clear</button></span>
                : <button type="button" className={btn} data-testid={`tables-outcome-pick-${i}`} onClick={() => setPicking(picking === i ? null : i)}>Creature…</button>}
            </div>
            {picking === i && <CreaturePicker onPick={(h) => { set(i, { ref: { segment: h.segment, slug: h.slug, name: h.name } }); setPicking(null); }} />}
          </li>
        ))}
      </ol>
      <div className="flex flex-wrap gap-1">
        <button type="button" className={btn} data-testid="tables-outcome-add" disabled={d.outcomes.length >= TABLE_LIMITS.outcomes}
          onClick={() => setD({ ...d, outcomes: [...d.outcomes, { text: '', weight: '1' }] })}>Add outcome</button>
        <span className="flex-1" />
        <button type="button" className={btn} onClick={onCancel}>Cancel</button>
        <button type="submit" className={btn} data-testid="tables-save">Save</button>
      </div>
      {said && <p role="alert" className="text-xs text-amber-300" data-testid="tables-editor-said">{said}</p>}
    </form>
  );
}

/** A creature from the compendium for an outcome to place: only the families a token may carry. */
function CreaturePicker({ onPick }: { onPick: (h: CompendiumHit) => void }) {
  const { campaignId } = useShell();
  const [q, setQ] = useState('');
  const [hits, setHits] = useState<CompendiumHit[]>([]);
  const [placeable, setPlaceable] = useState<string[] | null>(null);
  useEffect(() => { void placeableSegments(campaignId).then(setPlaceable); }, [campaignId]);
  useEffect(() => {
    const text = q.trim();
    if (!text || !placeable) { setHits([]); return; }
    const timer = setTimeout(() => {
      void searchCompendium(campaignId, text, null).then((a) => setHits(a.kind === 'ok' ? a.hits.filter((h) => placeable.includes(h.segment)) : []));
    }, 250);
    return () => clearTimeout(timer);
  }, [campaignId, q, placeable]);
  // Enter picks the top match — searching at once if the list has not arrived yet — and never submits the editor around it.
  const onEnter = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key !== 'Enter') return;
    e.preventDefault();
    if (hits[0]) { onPick(hits[0]); return; }
    const text = q.trim();
    if (!text || !placeable) return;
    void searchCompendium(campaignId, text, null).then((a) => {
      const found = a.kind === 'ok' ? a.hits.filter((h) => placeable.includes(h.segment)) : [];
      if (found[0]) onPick(found[0]); else setHits([]);
    });
  };
  return (
    <div className="space-y-1 rounded border border-stone-800 p-1" data-testid="tables-creature-picker">
      <input type="search" aria-label="Search for a creature" data-testid="tables-creature-search" placeholder="Goblin…" maxLength={100}
        className="w-full rounded bg-stone-800 px-2 py-1 text-xs" value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={onEnter} />
      <ul>
        {hits.map((h) => (
          <li key={`${h.segment}/${h.slug}`}>
            <button type="button" className="w-full text-left text-xs hover:bg-stone-800" data-testid={`tables-creature-${h.slug}`} onClick={() => onPick(h)}>{h.name} <span className="text-stone-500">· {h.family}</span></button>
          </li>
        ))}
      </ul>
    </div>
  );
}
